import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { useHistory } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, InputAdornment, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import { useTheme } from '@emotion/react';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { AttachFile, Block, Send, Visibility } from '@mui/icons-material';
import SupportChatReplyCard from './SupportChatReplyCard';
import {
  usePostMarkAsReadSupportChat,
  usePostSupportChatReply,
  useUpdateSupportChatFiles,
} from 'services/Data/SupportChatServices';
import { CartStatusEnumValue } from 'enums/CartStatusEnum';
import { ExteriorSignageQuoteStatusEnumValue } from 'enums/ExteriorSignageQuoteStatusEnum';
import { MerchandisingProgramQuoteStatusEnumValue } from 'enums/MerchandisingProgramQuoteStatusEnum';
import SupportFileRow from './SupportFileRow';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  supportChatNotification: {
    boxShadow: `${theme.palette.secondary.main}80 1px 10px 15px`,
    backgroundColor: `${theme.palette.secondary.main}25`,
    borderColor: theme.palette.common.black,
  },
}));

const SupportChatCard = ({
  supportChat,
  handleDeleteSupportChat,
  handleDeleteSupportChatReply,
  fromProfile,
  disableSupportChat,
  openUploadFileModal,
  handleRemoveFile,
  files,
  handleResetFile,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const { t } = useTranslation();

  const [reply, setReply] = useState('');
  const [showInput, setShowInput] = useState(false);

  const accountReducer = useSelector(state => state.AccountReducer);

  const { profile } = accountReducer;

  const { mutateAsync: postSupportChatReply, isPending: isPostingNewMessage } = usePostSupportChatReply();
  const { mutate: postMarkAsReadSupportChat, isPending: isPostingMarkAsReadSupportChat } = usePostMarkAsReadSupportChat();
  const { mutateAsync: uploadChatFiles } = useUpdateSupportChatFiles();

  const isGeneralChat = supportChat.supportChatType === 'General' || true;
  const cartId = supportChat.cart_Id;
  const cartNumber = supportChat.cartNumber;
  const cartStatus = supportChat.cartStatus;

  const exteriorSignageQuoteId = supportChat.exteriorSignageQuote_Id;
  const exteriorSignageQuoteNumber = supportChat.exteriorSignageQuoteNumber;
  const exteriorSignageQuoteStatus = supportChat.exteriorSignageQuoteStatus;

  const merchandisingProgramQuoteId = supportChat.merchandisingProgramQuote_Id;
  const merchandisingProgramQuoteNumber = supportChat.merchandisingProgramQuoteNumber;
  const merchandisingProgramQuoteStatus = supportChat.merchandisingProgramQuoteStatus;

  const isDisabledByStatus =
    (cartId && cartStatus > CartStatusEnumValue.Shipped) ||
    (exteriorSignageQuoteId && exteriorSignageQuoteStatus > ExteriorSignageQuoteStatusEnumValue.PendingPictures) ||
    (merchandisingProgramQuoteId && merchandisingProgramQuoteStatus > MerchandisingProgramQuoteStatusEnumValue.Enroll);

  const handleUpdateReply = value => {
    setReply(value);
  };

  const handleSaveReply = useCallback(() => {
    if (reply) {
      let newReply = {
        dateCreated: moment(),
        note: reply,
        supportChat_Id: supportChat.id,
      };

      postSupportChatReply(newReply).then(({ data: reply }) => {
        uploadChatFiles({ files, entityId: reply.id, type: BlobStorageTypeEnumValue.SupportChatReply }).then(_ => {
          setShowInput(false);
          setReply('');
          handleResetFile();
        });
      });
    }
  }, [postSupportChatReply, reply, supportChat.id, files, uploadChatFiles, handleResetFile]);

  const actions = [];

  if (supportChat.showNotificationDealerPortal) {
    actions.push({
      label: t('markAsRead'),
      onClick: () => {
        if (!isPostingMarkAsReadSupportChat) {
          postMarkAsReadSupportChat(supportChat.id);
        }
      },
    });
  }

  if (cartId) {
    actions.push({
      label: t('see_order'),
      onClick: () =>
        history.push(
          !isDisabledByStatus
            ? `/dealer/profile/awaiting-requests/order/${cartId}`
            : `/dealer/profile/request-histories/order/${cartId}`,
        ),
    });
  }

  if (exteriorSignageQuoteId) {
    actions.push({
      label: t('see_exterior_signage_quote'),
      onClick: () =>
        history.push(
          !isDisabledByStatus
            ? `/dealer/profile/awaiting-requests/exterior-signage-quote/${exteriorSignageQuoteId}`
            : `/dealer/profile/request-histories/exterior-signage-quote/${exteriorSignageQuoteId}`,
        ),
    });
  }

  if (merchandisingProgramQuoteId) {
    actions.push({
      label: t('see_merchandising_program_quote'),
      onClick: () =>
        history.push(
          !isDisabledByStatus
            ? `/dealer/profile/awaiting-requests/merchandising-program-quote/${merchandisingProgramQuoteId}`
            : `/dealer/profile/request-histories/merchandising-program-quote/${merchandisingProgramQuoteId}`,
        ),
    });
  }
  return (
    <GridItem xs={12} key={supportChat.id}>
      <CmtCard
        sx={{ height: '100%', padding: 0 }}
        className={`${classes.itemCard}${
          supportChat.showNotificationDealerPortal ? ` ${classes.supportChatNotification}` : ''
        }`}>
        <CmtCardHeader
          separator={{
            color: theme.palette.borderColor.dark,
          }}
          title={
            cartId
              ? t('associatedToOrder', { cartNumber })
              : exteriorSignageQuoteId
              ? t('associatedToExteriorSignageQuote', {
                  exteriorSignageQuoteNumber,
                })
              : merchandisingProgramQuoteId
              ? t('associatedToMerchandisingProgramQuote', {
                  merchandisingProgramQuoteNumber,
                })
              : isGeneralChat
              ? t('supportChatGeneral')
              : ''
          }
          subTitle={`${supportChat.userFullName} - ${moment(supportChat.dateCreated).format(t('LLL'))}`}
          actions={!fromProfile ? actions : []}
          actionHandleIcon={<Visibility />}
        />
        <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%', padding: 0 }}>
          <Box
            display="flex"
            flexDirection={'column'}
            justifyContent={'center'}
            sx={{ mt: 2, paddingLeft: 4, paddingRight: 4 }}>
            <Typography justifyContent={'center'} sx={{ mb: 1 }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: supportChat.note.replace(/\n/gi, '<br />'),
                }}
              />
            </Typography>
            <Box sx={{ height: '4rem', display: 'flex', flexDirection: 'row' }}>
              {supportChat.files?.map(f => (
                <SupportFileRow file={f} readOnly styleProps={{ ml: 0 }} />
              ))}
            </Box>

            <Box display="flex" flexDirection={'row'} justifyContent={'flex-end'}>
              <Button
                color="primary"
                disabled={isDisabledByStatus || disableSupportChat || showInput || isPostingNewMessage}
                onClick={() => setShowInput(!showInput)}>
                <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 } }}>
                  {t('reply')}
                </Typography>
              </Button>
              <Button
                color="danger"
                disabled={
                  isDisabledByStatus ||
                  disableSupportChat ||
                  showInput ||
                  isPostingNewMessage ||
                  supportChat.user_Id !== profile.person.user_Id
                }
                sx={{
                  mt: 1,
                }}
                onClick={() => handleDeleteSupportChat(supportChat)}>
                <Typography variant="h4">{t('delete')}</Typography>
              </Button>
            </Box>

            {showInput && (
              <>
                <CustomInput
                  labelText={t('reply')}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    type: 'text',
                    onChange: ({ target: { value } }) => handleUpdateReply(value),
                    value: reply,
                    disabled: false, //postingPurchaseOrderNoteReply,
                    onKeyDown: e => e.keyCode === 13 && e.shiftKey && handleSaveReply(),
                    endAdornment: (
                      <>
                        <InputAdornment position="start" onClick={openUploadFileModal}>
                          <Button
                            justIcon
                            round
                            color="primary"
                            style={{ zIndex: 1 }}
                            disabled={!reply || isPostingNewMessage}>
                            <AttachFile />
                          </Button>
                        </InputAdornment>
                        <InputAdornment position="start" onClick={handleSaveReply}>
                          <Button
                            justIcon
                            round
                            color="success"
                            style={{ zIndex: 1 }}
                            disabled={!reply || isPostingNewMessage}>
                            <Send />
                          </Button>
                        </InputAdornment>
                        <InputAdornment position="start" onClick={() => setShowInput(!showInput)}>
                          <Button justIcon round color="danger" style={{ zIndex: 1 }} disabled={isPostingNewMessage}>
                            <Block />
                          </Button>
                        </InputAdornment>
                      </>
                    ),
                  }}
                />

                <Box sx={{ height: '4rem', display: 'flex', flexDirection: 'row' }}>
                  {files?.map(f => (
                    <SupportFileRow file={f} handleRemoveFile={handleRemoveFile} />
                  ))}
                </Box>
              </>
            )}

            {supportChat?.supportChatReplies && supportChat?.supportChatReplies.length > 0 && (
              <GridItem xs={12}>
                {supportChat.supportChatReplies
                  .slice()
                  .sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated))
                  .map(r => (
                    <>
                      <Divider sx={{ my: 2 }} color={theme.palette.secondary.main} />
                      <SupportChatReplyCard
                        key={r.id}
                        reply={r}
                        supportChat={supportChat}
                        handleDeleteSupportChatReply={handleDeleteSupportChatReply}
                        disableSupportChat={isDisabledByStatus || disableSupportChat}
                      />
                    </>
                  ))}
              </GridItem>
            )}
          </Box>
        </CmtCardContent>
      </CmtCard>
    </GridItem>
  );
};

export default SupportChatCard;
